import React, { type ReactElement, useEffect, useState } from 'react';
import {
  type ComboBoxItem,
  type SharedComboBoxProps,
  type SharedInitialComboBoxProps
} from 'atom-components/combo-boxes/types';
import Loading from 'atom-components/loading';
import ControlledMultiComboBox from './controlled-multi-combo-box';

type InitialComboBoxProps = SharedInitialComboBoxProps & {
  initialSelectedItems: ComboBoxItem[];
  externallySelectedItems?: ComboBoxItem[];
  onChange?: (items: ComboBoxItem[]) => void;
};

type UncontrolledMultiComboBoxProps = InitialComboBoxProps & SharedComboBoxProps;

function UncontrolledMultiComboBox({
  initialSelectedItems,
  externallySelectedItems,
  reinitialisable,
  clearCondition,
  id,
  allItems,
  placeholder,
  className,
  hasError,
  clearable,
  disabled,
  onBlur,
  onChange
}: UncontrolledMultiComboBoxProps): ReactElement {
  const [selectedItems, setSelectedItems] = useState<ComboBoxItem[]>(initialSelectedItems || []);

  useEffect(() => {
    if (clearCondition) {
      setSelectedItems([]);
    }
  }, [clearCondition, setSelectedItems]);

  // If the selected items are changed from outside the combo box, for example via applied filters,
  // and reinitialisation is enabled, we should reset the internal selected items to the externally
  // selected items:
  useEffect(() => {
    if (reinitialisable && externallySelectedItems) {
      const haveExternalItemsDiveraged =
        selectedItems.length !== externallySelectedItems.length ||
        !selectedItems.every((item, index) => item.id === externallySelectedItems[index].id);

      if (haveExternalItemsDiveraged) {
        setSelectedItems([...externallySelectedItems]);
      }
    }
  }, [externallySelectedItems, reinitialisable, selectedItems]);

  return (
    <ControlledMultiComboBox
      id={id}
      allItems={allItems}
      placeholder={placeholder}
      className={className}
      hasError={hasError}
      clearable={clearable}
      disabled={disabled}
      selectedItems={selectedItems}
      setSelectedItems={(items: ComboBoxItem[]): void => setSelectedItems(items)}
      onBlur={(): void => (onBlur ? onBlur() : undefined)}
      onChange={(items): void => {
        setSelectedItems(items);
        if (onChange) {
          onChange(items);
        }
      }}
    />
  );
}

type MultiComboBoxProps = {
  loading?: boolean;
} & InitialComboBoxProps &
  SharedComboBoxProps;

/**
 * A wrapper around the MultiComboBox which turns it into an uncontrolled component that manages
 * it's own state, keeping the details of setting state inside the component.
 */
export default function MultiComboBox({
  initialSelectedItems,
  externallySelectedItems,
  reinitialisable,
  clearCondition,
  loading,
  id,
  allItems,
  className,
  hasError,
  clearable,
  disabled,
  onBlur,
  onChange
}: MultiComboBoxProps): ReactElement {
  // This can be used to ensure that initialSelectedItems is populated only after loading is
  // completed:
  if (loading) {
    return <Loading />;
  }

  return (
    <UncontrolledMultiComboBox
      initialSelectedItems={initialSelectedItems}
      externallySelectedItems={externallySelectedItems}
      reinitialisable={reinitialisable}
      clearCondition={clearCondition}
      id={id}
      allItems={allItems}
      className={className}
      hasError={hasError}
      clearable={clearable}
      disabled={disabled}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
}
